import type { Money } from "@workspace/accounting/types";
import type {
	Menu,
	OptionGroup,
	Product,
	ProductIngredient,
	ProductOptionAssignment,
} from "@workspace/product-catalog-types";
import type { TerminalReader } from "@workspace/stripe";
import type { StaffMember } from "@workspace/venue-management-staff";

export interface SelectedOptionItem {
	id: string;
	name: string;
	priceImpact: Money;
}

export interface SelectedOptionGroup {
	id: string;
	name: string;
	type: "Single" | "Multi";
	selectedItems: SelectedOptionItem[];
}

export interface OrderItem {
	id: string;
	productId: string;
	productVariantId: string;
	name: string;
	price: Money;
	quantity: number;
	status: "Pending" | "Preparing" | "Ready" | "Delivered" | "Cancelled";
	refundedQuantity?: number;
	menuId: string;
	queueId?: string | null;
	courseId?: string | null;
	sentToKitchen: boolean;
	/** HA-600 AC6 — the product's prep time in minutes, taken by the server when the line was sent. */
	preparationTime?: number | null;
	notes?: string;
	selectedOptions?: SelectedOptionGroup[];
	removedIngredients?: ProductIngredient[];
}

export interface Payment {
	id: string;
	orderId: string;
	amount: Money;
	method: "cash" | "card" | "mobile" | "gift-card" | "other";
	status: "Pending" | "Completed" | "Failed" | "Refunded";
	timestamp: string;
	reference?: string;
	tip?: Money;
	cardDetails?: {
		last4: string;
		brand: string;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	paymentProviderInfo?: any;

	externalId?: string | null;

	/**
	 * HA-554 Part 2 — a Pay Tab card payment's share: the tab payment it is part of (the card payment the server knows).
	 * Null for any other payment.
	 */
	tabPaymentId?: string | null;
}

export interface OrderRefund {
	id: string;
	timestamp: string;
	staff: { id: string; label: string };
	subtotal: Money;
	tax: Money;
	total: Money;
	method: string;
	reason: string;
	notes?: string | null;
	items: Array<{ itemId: string; quantity: number }>;
	// Stripe refund status fields
	status: RefundStatus;
	stripeRefundId?: string | null;
	stripePaymentIntentId?: string | null;
	errorMessage?: string | null;
}

/** HA-849: the server's figures for an order (OrderMoney). */
export interface OrderFigures {
	subtotal: Money;
	tax: Money;
	total: Money;
	paid: Money;
	outstanding: Money;
	/** HA-357: the service charge in the total. It's not tax (no VAT on it). */
	serviceCharge?: Money;
	/** HA-15 A1: every discount's £ amount as the server priced it (each item discount, and the whole-order one). The till never works these out. */
	discounts?: PricedDiscount[];
	/** HA-15: the discounts in total (item discounts and the whole-order one). */
	discount?: Money;
	/** HA-15 A1: the subtotal after the discounts; `subtotal` stays the gross. */
	subtotalAfterDiscounts?: Money;
	/** HA-15: each line as priced: the bounds a discount is checked against are read from these. */
	lines?: PricedLine[];
}

/** HA-15 A5: what a discount is on: the whole order, or one line. */
export type DiscountTarget =
	{ scope: "Order" } | { scope: "Line"; lineId: string };

/** HA-15 A5: a flat discount's value is in minor units (an integer); a percentage is a decimal with at most 2dp. */
export type DiscountType = "Flat" | "Percentage";

/** HA-15: a discount as the server stores it. There is at most one per target. */
export interface OrderDiscount {
	target: DiscountTarget;
	type: DiscountType;
	value: number;
	reason: string;
	actor?: { id: string; label?: string } | null;
	at?: string;
}

/** HA-15 A1: a discount's priced amount in minor units (an integer), worked out by the server; the till never works one out. */
export interface PricedDiscount {
	target: DiscountTarget;
	amount: number;
}

/**
 * HA-15 / HA-658: one line as the server priced it (`OrderPricing.PricedLine`), all amounts in minor units. `gross` is the line's
 * worth, `charged` what the order charges for it (0 when comped; less its own discount and its share of the whole-order one).
 * Keyed by `itemId`, which is the line target's `lineId` in a discount.
 */
export interface PricedLine {
	itemId: string | null;
	gross: number;
	charged: number;
	comped: boolean;
	taxRatePercent?: number | null;
	tax: number;
	itemDiscount: number;
	orderDiscountShare: number;
}

/** HA-15 A5: the body of the apply (and modify) route. */
export interface ApplyOrderDiscountInput {
	target: DiscountTarget;
	type: DiscountType;
	value: number;
	reason: string;
}

/** HA-15 A5: the body of the remove route. Removing takes no reason. */
export interface RemoveOrderDiscountInput {
	target: DiscountTarget;
}

/** HA-15: both discount routes answer with the updated Order, as every line write does; its `figures` and `discounts` are the server's. */
export type OrderDiscountResponse = Order;

/**
 * HA-658: a comp on an order, as the server stores it (`OrderComp`): the lines it makes free (`itemIds`, one for a line comp), why,
 * and what they were worth. The till reads it for marks and never works a price from it: money is the server's figures.
 */
export interface OrderComp {
	compId: string;
	scope: "Line" | "Order";
	itemIds: string[];
	reason: string;
	amount: Money;
	actor?: { id: string; label?: string } | null;
	at?: string;
}

/** HA-658: the body of the line and whole-order comp routes. A reason is required; Remove Comp takes none. */
export interface CompInput {
	reason: string;
}

/** HA-265: the most recent single removal on an order, kept by the server so it can be restored (one level). */
export interface LastRemoved {
	removalId: string;
	item: OrderItem;
	index: number;
	wasSent: boolean;
	returnedToStock: boolean;
}

export interface Order {
	id: string;
	tabId?: string | null;
	tableId?: string;
	tableName?: string;
	type?: string; // Make this optional
	status:
		| "Open"
		| "Paid"
		| "Cancelled"
		| "Refunded"
		| "PartiallyRefunded"
		| "Completed"
		| "Closed"
		| "Dismissed";
	items: OrderItem[];
	subtotal: Money;
	outstanding?: Money | null;
	totalPaid?: Money | null;
	change?: Money | null;
	tax: number;
	total: Money;
	/** HA-849: the order's money as the server computes it, on every line write's response. */
	figures?: OrderFigures;
	/** HA-265: set while a removal can be restored; the server clears it on restore, complete, close, dismiss. */
	lastRemoved?: LastRemoved | null;
	/** HA-849 PR 3: the tax rate the order was sold at (percent, exclusive); refunds are priced at it. */
	taxRate?: number;
	tip?: number;
	currency: string;
	/** HA-658: the order's comps, from the server. A comped line reads Comp'd (£0); every figure is the server's. */
	comps?: OrderComp[];
	/** HA-658: at least one line, and every line comped. */
	fullyComped?: boolean;
	/** HA-658 R1: comps brought the order to £0.00 with no payment: it is Completed, and no sale was recorded. */
	completedByComps?: boolean;
	/** HA-885: the discounts the order carries (one per target), as the server stores them. The amounts are in `figures.discounts`. */
	discounts?: OrderDiscount[];
	/**
	 * HA-885: set while the till's own placeholder totals would be wrong (the order has discounts and its lines changed): the last
	 * server figures stay on show, marked stale, until the server answers.
	 */
	figuresStale?: boolean;
	createdAt: string;
	updatedAt: string;
	completedAt?: string;
	server: StaffMember;
	payments: Payment[];
	refunds?: OrderRefund[];
	refundedTotal?: Money;
	refundableBalance?: Money;
	/** HA-357: the service charge in the total, as money (the venue's copy, and each line write's figures). Not tax. */
	serviceChargeAmount?: Money;
	/** HA-357: the venue's service charge as the order was opened (the till's running total uses it until the server answers). */
	serviceCharge?: {
		enabled: boolean;
		type: "Percentage" | "Fixed";
		value: number;
	} | null;
	/** HA-357 slice C (AC9): a staff member has taken the charge off this order; it's back on when they add it. */
	serviceChargeRemoved?: boolean;
	closeReason?: CloseReason | null;
	closeNotes?: string | null;
	firedCourseIds?: string[];
	pendingCourseFire?: {
		courseId: string;
		initiatedAt: string;
		deviceId?: string | null;
	} | null;
	customer?: {
		name: string;
		phone?: string;
		address?: string;
	};
	activeSplitGroup?: ActiveSplitGroup | null;
	isFrozen?: boolean;
}

export interface ActiveSplitGroup {
	id: string;
	definition: SplitDefinitionInput;
	splits: ActiveSplit[];
}

export interface ActiveSplit {
	id: string;
	index: number;
	amount: Money;
	status: "Open" | "Paid" | "Refunded";
	paymentId?: string | null;
}

export type SplitMode = "Equal" | "Percent" | "Items" | "Amounts";

export interface SplitDefinitionInput {
	mode: SplitMode;
	count: number;
	percentages?: number[] | null;
	itemIdsPerSplit?: string[][] | null;
	amountsPerSplit?: Money[] | null;
}

export type CloseReason =
	| "CustomerWalkedOut"
	| "CustomerDispute"
	| "CancelledByStaff"
	| "DuplicateOrder"
	| "StaffMistake"
	| "TabClosed"
	| "TestTraining";

export interface OrderSplit {
	id: string;
	orderId: string;
	splitMethod: "none" | "items" | "percentage" | "number";
	splitCount: number;
	splitItems?: Record<string, OrderItem[]>;
}
export interface CashDrawerTransaction {
	id: string;
	type: "sale" | "refund" | "payout" | "deposit" | "correction";
	amount: Money;
	timestamp: string;
	description: string;
	userId?: string;
}

/** The venue's drawer: the one the till opens without a sale, and those openings (HA-954: nothing else feeds it). */
export interface CashDrawer {
	id: string;
	transactions: CashDrawerTransaction[];
}

export type TabStatus = "Open" | "Closed";

// Matches C# `VenueManagement.Core.Epos.RefundStatus`
export type RefundStatus = "Pending" | "Processing" | "Succeeded" | "Failed";

// Matches C# `VenueManagement.Core.Epos.VenueEposData.Tab`
export interface Tab {
	id: string;
	number: number;
	name: string | null;
	section: string;
	covers: number;
	status: TabStatus;
	notes?: string | null;
	createdByUserId?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	/** HA-554 — the orders on the tab, in the order they joined (open and closed ones). */
	orders: string[];
	/** HA-554 — the server's figures for the tab: its total, and what's still outstanding. */
	total?: Money | null;
	outstanding?: Money | null;
}

// Matches C# `VenueManagement.Core.Epos.TabOrderLine` (HA-554)
export interface TabOrderLine {
	id: string;
	status: Order["status"];
	total: Money;
	outstanding: Money;
	serverId?: string | null;
	createdAt: string;
	/** HA-658: what the order's comps made free (£0.00 with none), and whether every line is comped. */
	compedAmount?: Money;
	fullyComped?: boolean;
	/** HA-15: the order's discounts and their total, so the tab can show each order's. */
	discounts?: OrderDiscount[];
	discountTotal?: Money;
}

// Matches C# `VenueManagement.Core.Epos.TabDetailResponse` (HA-554): the tab, its orders oldest first, and its figures.
export interface TabDetail {
	tab: {
		id: string;
		venueId: string;
		number: number;
		name: string | null;
		status: TabStatus;
		orderIds: string[];
		closedAt?: string | null;
		/** HA-554 Part 2 — the Pay Tab payments recorded on the tab, each with its allocation across the orders. */
		payments: TabPaymentRecord[];
		/** HA-554 Part 2 — a Pay Tab card payment in flight, holding the orders its allocation reaches. */
		cardPaymentsInFlight: TabCardPaymentInFlight[];
		/** HA-357 slice D — the tab's own fixed service charge line (null: it has never had one). */
		serviceChargeLine?: TabServiceChargeLine | null;
		/** HA-789 — the refunds of its Pay Tab payments. */
		refunds?: TabRefund[];
	};
	orders: TabOrderLine[];
	/** HA-15 A3: `discount` is the discounts of the tab's chargeable orders, summed by the server; the till never adds up the orders' own. */
	figures: {
		total: Money;
		outstanding: Money;
		serviceCharge?: Money;
		discount?: Money;
	};
}

// Matches C# `VenueManagement.Core.Epos.TabServiceChargeLine` (HA-357 slice D): with a fixed charge, one line on the tab
// rather than one per order; paid last; Remove and Add at the till (epos:tabs:manage).
export interface TabServiceChargeLine {
	amount: Money;
	active: boolean;
	removedByStaff: boolean;
	paid: Money;
}

// Matches C# `DiverLog.Common.Accounting.Payments.TabPaymentShare` (HA-554 Part 2): one order's share of a Pay Tab payment.
export interface TabPaymentShare {
	orderId: string;
	paymentId: string;
	amount: Money;
	/** HA-859 — this order's part of the payment's tip. */
	tip?: Money | null;
}

// Matches C# `DiverLog.Common.Accounting.Payments.TabPaymentRecorded` (HA-554 Part 2).
export interface TabPaymentRecord {
	tabPaymentId: string;
	method: "cash" | "card";
	amount: Money;
	allocation: TabPaymentShare[];
	timestamp: string;
	tendered?: Money | null;
	externalId?: string | null;
	/** HA-357 slice D — the part that went to the tab's own service charge line (paid last). */
	lineAmount?: Money | null;
	/** HA-859 — the payment's tip, on top of its amount, split across the orders it paid. */
	tip?: Money | null;
}

// Matches C# `VenueManagement.Core.Epos.TabRefundOrderPart` (HA-789): one order's part of a Pay Tab refund.
export interface TabRefundOrderPart {
	orderId: string;
	sharePaymentId: string;
	orderRefundId: string;
	amount: Money;
	tip?: Money | null;
}

// Matches C# `VenueManagement.Core.Epos.TabRefund` (HA-789): a refund of a Pay Tab payment, spread over what it paid.
export interface TabRefund {
	refundId: string;
	tabPaymentId: string;
	method: "cash" | "card";
	total: Money;
	orders: TabRefundOrderPart[];
	line: Money;
	status: "Pending" | "Processing" | "Succeeded" | "Failed";
	attempt: number;
	stripeRefundId?: string | null;
	errorMessage?: string | null;
	timestamp: string;
}

// Matches C# `VenueManagement.Core.Epos.TabPaymentRefundResult` (HA-789).
export interface TabPaymentRefundResult {
	refund: TabRefund | null;
	tab: TabDetail;
}

// Matches C# `DiverLog.Common.Accounting.Payments.TabCardPaymentStarted` (HA-554 Part 2).
export interface TabCardPaymentInFlight {
	tabPaymentId: string;
	amount: Money;
	allocation: TabPaymentShare[];
}

// Matches C# `VenueManagement.Core.Epos.TabPaymentResponse` (HA-554 Part 2): what a Pay Tab payment answers with, all from
// the server: what it applied, the change, its allocation, and the tab after it.
export interface TabPaymentResponse {
	tabPaymentId: string;
	applied: Money;
	change?: Money | null;
	allocation: TabPaymentShare[];
	tab: TabDetail;
}

// Matches C# `VenueManagement.Core.Epos.PrintReceiptResponse` for a Tab print (HA-360 Part B): the server decides the Tab
// document from the tab's state, and a second print of the same document is a duplicate.
export interface PrintTabResult {
	documentType: "TabBill" | "TabReceipt";
	duplicate: boolean;
	counter: number;
	absorbed?: boolean;
}

// Matches C# `VenueManagement.Core.Epos.ClosedTabSummary` (HA-360 Part B): a tab closed today, to reprint its Tab receipt.
export interface ClosedTabSummary {
	id: string;
	number: number;
	name?: string | null;
	section: string;
	closedAt: string;
	total: Money;
}

// Matches C# `VenueManagement.Core.Epos.TabMembershipResponse` (HA-554): what Add to tab and Move answer with — the order
// and each tab it touched, merged into the till's state in place (mergeTabMembership).
export interface TabMembershipResponse {
	order: Order | null;
	tabs: TabDetail[];
}

// Matches C# `VenueManagement.Core.Epos.OpenDrawerReason`
export type OpenDrawerReason = "Float" | "Payout" | "Audit" | "Other";

// Matches C# `VenueManagement.Core.Epos.OpenCashDrawerWithoutSaleInput`
export interface OpenCashDrawerWithoutSaleInput {
	drawerId: string;
	reason: OpenDrawerReason;
	reasonText?: string | null;
}

// Matches C# `VenueManagement.Core.Epos.TabInput` (JSON camelCase)
export interface TabInput {
	tabId?: string | null;
	number: number;
	name: string | null;
	section: string;
	covers: number;
	notes?: string | null;
	createdAt?: string | null;
}

// Matches C# `VenueManagement.Core.Epos.TabUpdateInput` (JSON camelCase)
export interface TabUpdateInput {
	name: string | null;
	notes?: string | null;
	updatedAt?: string | null;
}

export interface ServerShift {
	id: string;
	server: string;
	startTime: string;
	endTime?: string;
	orders: number;
	sales: number;
	cash: number;
	card: number;
	other: number;
	tips: number;
}

export interface TopSellingItem {
	name: string;
	quantity: number;
	revenue: number;
}

export interface SalesSummary {
	today: {
		total: number;
		orders: number;
		averageOrder: number;
		topSellingItems: TopSellingItem[];
	};
	thisWeek: {
		total: number;
		orders: number;
		comparedToLastWeek: number;
	};
	thisMonth: {
		total: number;
		orders: number;
		comparedToLastMonth: number;
	};
}

export interface EposSettings {
	taxRate: number;
	receiptHeader: string;
	receiptFooter: string;

	menus: Menu[];
	products: Product[];
	courses: EposCourse[];
	productOptions: {
		optionGroups: OptionGroup[];
		productOptionAssignments: ProductOptionAssignment[];
	};
	enabled: boolean;
}

export interface EposCourse {
	id: string;
	name: string;
	autoFire: boolean;
	sortOrder: number;
	status: "Active" | "Inactive";
}

export interface UnsentItemDetail {
	itemId: string;
	name: string;
	quantity: number;
	price: Money;
}

export interface UnsentItemsGroup {
	courseId: string | null;
	courseName: string;
	items: UnsentItemDetail[];
}

export interface UnsentItemsResponse {
	groups: UnsentItemsGroup[];
	total: Money;
	itemCount: number;
}

export interface ClosedOrderListItem {
	id: string;
	tabId?: string | null;
	tabNumber?: number | null;
	tabName?: string | null;
	total: Money;
	status: Order["status"];
	closeReason?: string | null;
	closeNotes?: string | null;
	completedAt?: string | null;
	createdAt: string;
}

export interface ClosedOrderSearchResult {
	count: number;
	items: ClosedOrderListItem[];
	page: number;
	pageSize: number;
	hasNextPage: boolean;
}

export interface OrderDetailResponse {
	id: string;
	venueId: string;
	status: Order["status"];
	type: string;
	currency: string;
	items: OrderItem[];
	tax: number;
	createdAt: string;
	updatedAt?: string | null;
	payments: Payment[];
	tableId?: string | null;
	tableName?: string | null;
	tabId?: string | null;
	tabNumber?: number | null;
	tabName?: string | null;
	tip?: number | null;
	completedAt?: string | null;
	customer?: Order["customer"];
	refunds?: OrderRefund[];
	subtotal: Money;
	total: Money;
	totalPaid: Money;
	refundedTotal: Money;
	refundableBalance: Money;
	/** HA-357: the service charge in the total, as money. It's not tax, so the VAT split takes it out first. */
	serviceChargeAmount?: Money;
	closeReason?: string | null;
	closeNotes?: string | null;
}

export interface CourseBadgeColours {
	notStarted: string;
	inProgress: string;
	allSent: string;
}

export const defaultCourseBadgeColours: CourseBadgeColours = {
	notStarted: "#9CA3AF",
	inProgress: "#F59E0B",
	allSent: "#22C55E",
};

export interface RecentPrintFailure {
	jobId: string;
	venueId: string;
	reasonCode: "NO_DEVICE_ALLOCATED" | "MALFORMED_HOSTNAME" | string;
	occurredAt: string;
	message?: string | null;
}

export interface EposData {
	settings: EposSettings;
	activeTabs: Tab[];
	orders: Order[];
	currentCashDrawer: CashDrawer;
	activeShifts: ServerShift[];
	salesSummary: SalesSummary;
	terminals: TerminalReader[];
	courseBadgeColours?: CourseBadgeColours;
	recentPrintFailures?: RecentPrintFailure[];
	/** HA-858 — the venue takes tips now (its own setting, else the tenant default, else off). */
	tipsEnabled: boolean;
}
